/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, take, map, of, switchMap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Router } from '@angular/router';

@Injectable()
export class ClaimService {
	id = Math.random();
	constructor(private http: HttpClient, private router: Router) {}

	getAllClaims(): Observable<any> {
		return this.http.get<any[]>(
			environment.api_url + 'v1/staff_action/get_summary/'
			// { params: { page: '1' } }
		);
	}

	updateClaim(claim): Observable<any> {
		return this.http.post(`${environment.api_url}v1/claim_action/update_claim/`, claim);
	}

	getClaimAppointments(params: { claim_id: number }): Observable<any> {
		return this.http.post(`${environment.api_url}v1/claim_action/get_claim_appointments`, {
			claim_id: params.claim_id
		});
	}

	getFullClaim(claim_id: number): Observable<any> {
		// return this.http.get<any[]>(environment.api_url + `v1/claim/${claimId}/`);
		return this.http.post(`${environment.api_url}v1/claim_action/get_claim/`, { claim_id, full: false });
	}

	getFullClaimWithJobs(claim_id: number): Observable<any> {
		// return this.http.get<any[]>(environment.api_url + `v1/claim/${claimId}/`);
		return this.http.post(`${environment.api_url}v1/claim_action/get_claim/`, { claim_id, full: true });
	}

	/**
	 * Create a claim.
	 *
	 * NOTE:
	 * - For normal submissions we keep the existing behaviour: after create_claim,
	 *   if the API returns loan_information + claim id, we immediately call update_claim.
	 * - For server drafts we must ONLY call create_claim with new_state: 8 and NEVER call update_claim.
	 *
	 * For nav-config `followUpFailCalls` (e.g. Pinggo Submit → saveAsLocalDraft) to work reliably,
	 * this method must surface a clear `success: false` result when the post-create update fails,
	 * so that the engine can emit MAKE_SERVER_CALL_FAIL and trigger the fail follow-ups.
	 */
	createClaim(claim, options?: { skipPostCreateUpdate?: boolean }) {
		// console.log('createClaim 41', claim);
		// return of(null);
		return this.http.post(`${environment.api_url}v1/claim_action/create_claim/`, claim).pipe(
			switchMap((response: any) => {
				// Only explicit draft/opt-out flows should skip post-create update calls
				if (options?.skipPostCreateUpdate === true) {
					return of(response);
				}

				const hasLoanInformation = !!response?.payload?.loan_information;
				const claimId = response?.payload?.id;

				// If the create response doesn't have what we need for an update,
				// just surface the original response (success or failure) as-is.
				if (!hasLoanInformation || !claimId) {
					return of(response);
				}

				const request = { ...claim, new_state: 138, claim_id: claimId };
				console.log('UPDATE CLAIM REQUEST', request);
				return this.http.post(`${environment.api_url}v1/claim_action/update_claim/`, request).pipe(
					map((updateRes: any) => {
						// If the post-create update fails, propagate a failure shape so that
						// nav-level followUpFailCalls can run (e.g. saveAsLocalDraft).
						if (updateRes?.success === false) {
							return {
								success: false,
								payload: updateRes?.payload ?? null,
								originalResponse: response
							};
						}

						// Normal happy path: enrich the original create response with update payload.
						return {
							...response,
							updatePayload: updateRes?.payload
						};
					})
				);
			})
		);
	}

	/**
	 * Create a server draft.
	 * This MUST only call create_claim with new_state: 8 and MUST NOT trigger update_claim.
	 */
	createClaimDraft(claim): Observable<any> {
		return this.createClaim({ ...(claim ?? {}), new_state: 8 }, { skipPostCreateUpdate: true });
	}

	cancelClaim(data: any) {
		return this.http.post(`${environment.api_url}v1/claim_action/cancel_claim`, data);
	}

	reopenClaim(data: any) {
		return this.http.post(`${environment.api_url}v1/claim_action/reopen_claim`, data);
	}

	getClaimExtra(claimId: number): Observable<any> {
		const body = { claim_id: claimId };
		return this.http.post(environment.api_url + `v1/claim_action/get_summary_extras/`, body).pipe(
			take(1),
			map((extras: any) => extras.payload)
		);
	}

	getCloseClaimInfo(params: any): Observable<any> {
		return this.http.post(environment.api_url + `v1/claim_action/can_finalise_claim/`, params);
	}
	finaliseClaim(params: { claim_id: number }): Observable<any> {
		return this.http.post(environment.api_url + `v1/claim_action/finalise_claim/`, params);
	}

	policyHistoryLookup(data: { claim_id: string }) {
		return this.http.post(`${environment.api_url}v1/claim_action/get_claim_history/`, {
			search: '',
			claim_id: data?.claim_id
		});
	}

	searchClaims(params: { search: string; active: boolean }) {
		return this.http.post<any>(environment.api_url + `v1/claim_action/search_claims/`, { ...params });
	}

	checkCatastrophe(param: { claim_type: number; postal_code: number; loss_date: string }) {
		return this.http.post(environment.api_url + `v1/cat_action/check_catastrophe/`, param);
	}

	getExcessDetailsService(data: { claim_id: number }) {
		return this.http.post<any>(`${environment.api_url}v1/claim_action/get_excess_details/`, data);
	}
}
